/**
 * Contract types of the Prompt Forge API, as consumed by the SPA.
 *
 * The members mirror the application DTOs one-for-one. Enums travel as their **names** because the API installs
 * `JsonStringEnumConverter` (`Api/Extensions/ServiceCollectionExtensions.cs`), so the string unions below are
 * exhaustive rather than cosmetic.
 *
 * The OpenAPI-generated `types/api.d.ts` of API surface §7 will replace these hand-written contracts in
 * Phase 8; until then this file is the checked-in source of truth, and the status document records the
 * deviation.
 */

import type { PublicationStatus } from '@/features/publication/api/publicationApi';

/** Depth of the generated prompt — must materially change the rendered output (ADR-0015). */
export const PROMPT_LEVELS = ['Simple', 'Standard', 'Expert'] as const;
export type PromptLevel = (typeof PROMPT_LEVELS)[number];

/** AI provider families supported by the abstraction layer (ADR-0006). */
export const AI_PROVIDER_KINDS = [
  'OpenAi',
  'Anthropic',
  'GoogleGemini',
  'Ollama',
  'CustomOpenAiCompatible',
] as const;
export type AiProviderKind = (typeof AI_PROVIDER_KINDS)[number];

/** Colour-scheme preference stored on the account. */
export const THEME_PREFERENCES = ['System', 'Light', 'Dark'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** UI locale stored on the account. */
export const PROFILE_LOCALES = ['fa-IR', 'en-US'] as const;
export type ProfileLocale = (typeof PROFILE_LOCALES)[number];

/** Roles granted by ASP.NET Core Identity (`PromptForgeRoles`). */
export const PROMPT_FORGE_ROLES = ['Admin', 'ProUser', 'User'] as const;
export type PromptForgeRole = (typeof PROMPT_FORGE_ROLES)[number];

/** Feature flags and quotas derived from the role set (`EntitlementsDto`). */
export interface Entitlements {
  canPublishTemplates: boolean;
  canUseExpertLevel: boolean;
  /** −1 means unlimited. */
  monthlyPromptQuota: number;
  /** −1 means unlimited. */
  dailyAiRequestQuota: number;
}

/** Public representation of an account (`UserProfileDto`). */
export interface UserProfile {
  id: string;
  /**
   * E-mail address of the account, or `null` when it has none (TASK-0064 §7).
   *
   * An account provisioned through Organisation SSO carries the address only when the authority released one; the screens show the
   * public handle or the display name instead, never a placeholder.
   */
  email: string | null;
  displayName: string;
  avatarUrl: string | null;
  locale: string;
  theme: ThemePreference;
  preferredProviderKind: AiProviderKind | null;
  preferredModelId: string | null;
  preferredPromptLevel: PromptLevel;
  emailConfirmed: boolean;
  isBanned: boolean;
  roles: string[];
  highestRole: string | null;
  entitlements: Entitlements;
  createdAtUtc: string;
  lastLoginAtUtc: string | null;
  /**
   * Public handle the account is known by, or `null` while the owner has not registered one (TASK-0060).
   *
   * Distinct from `email`, which is the sign-in credential and never published.
   */
  publicIdentifier: string | null;
  /**
   * Whether the owner still has to register a handle.
   *
   * Computed by the server (`UserProfileDto.CanRegisterPublicIdentifier`) so the screen never re-derives the rule.
   */
  canRegisterPublicIdentifier: boolean;
  /**
   * Registration and verification state under the policy in force (TASK-0062).
   *
   * The banner and the route guard branch on this object rather than on `emailConfirmed`: the policy can require the mobile number,
   * and a boolean could not say which identifier is missing.
   */
  verification: VerificationState | null;
}

/** Token pair returned by sign-in and refresh; the refresh token is also set as an HttpOnly cookie. */
export interface AuthSession {
  accessToken: string;
  expiresIn: number;
  accessTokenExpiresAtUtc: string;
  refreshToken: string;
  refreshExpiresIn: number;
  refreshTokenExpiresAtUtc: string;
  user: UserProfile;
}

/** Outcome of a successful registration. */
export interface RegisterUserResult {
  userId: string;
  email: string;
  verificationEmailSent: boolean;
  /** Present only in Development, where no mail gateway is required. */
  emailVerificationToken?: string | null;
  /**
   * The verification state that follows the registration (TASK-0062).
   *
   * The panel renders from this object, so it knows which channel to ask about, when the challenge expires and how many attempts are
   * left without a second request.
   */
  verification: VerificationState;
}

/** Outcome of an e-mail verification. */
export interface VerifyEmailResult {
  userId: string;
  /** The address that was verified, or `null` for an account that has none (TASK-0064 §7). */
  email: string | null;
  alreadyConfirmed: boolean;
}

/** Outcome of a verification-mail resend. */
export interface ResendVerificationResult {
  delivered: boolean;
}

/** The identifier a new account must register with (TASK-0062). */
export type VerificationChannel = 'Email' | 'Mobile';

/**
 * What the caller still has to do about their identifier (TASK-0062).
 *
 * `Unregistered` is distinct from `Pending` because the two need different screens: a password-less account created by a provider
 * sign-in has no mobile number at all on the mobile channel, so it has to register one before a code can be sent.
 */
export type VerificationStateKind =
  'Anonymous' | 'Verified' | 'Pending' | 'Unregistered' | 'Banned';

/** How an e-mail verification is delivered, or `null` when both are accepted. */
export type VerificationDeliveryMethod = 'Code' | 'Link';

/** Who produced the evidence: the owner, or an administrator on their behalf. */
export type IdentifierVerificationMethod =
  'Unverified' | 'SelfService' | 'Administrator' | 'OrganisationSso';

/**
 * The sign-in methods an administrator may enable (TASK-0063).
 *
 * `Local` is the password form, `Google` and `Organisation` are the two external providers ADR-0027 shipped. The value is the domain
 * identifier the API stores and echoes — never a translated label (ADR-0024), and never a provider's configuration key.
 */
export type SignInMethod = 'Local' | 'Google' | 'Organisation';

/**
 * The policy in force, as `GET /auth/policy` publishes it (TASK-0062, extended by TASK-0063).
 *
 * The screens render from this object and never from a local guess: an administrator can switch the channel without a deployment,
 * and a client that assumed e-mail would show the wrong field the moment they did.
 */
export interface AuthPolicy {
  activeChannel: VerificationChannel;
  emailDeliveryMethod: VerificationDeliveryMethod | null;
  verificationValiditySeconds: number;
  maxAttempts: number;
  resendCooldownSeconds: number;
  codeLength: number;
  policyVersion: number;
  externalProviders: ExternalProviderAvailability[];
  /** The methods the policy enables. `null` on an older answer, which means "every method this build implements". */
  signInMethods?: SignInMethod[] | null;
}

/** Whether one provider is offered, and whether this deployment can actually reach it. */
export interface ExternalProviderAvailability {
  provider: string;
  enabled: boolean;
  deliverable: boolean;
}

/**
 * The public OIDC client configuration the SPA drives the organisation flow with (`GET /auth/organisation/client`, TASK-0064).
 *
 * A public client has no secret: the client id and the registered callback URI travel in the authorization request the browser
 * makes, so the answer leaks nothing. The SPA builds its OIDC client from exactly these values — the same ones the backend
 * validates the returned tokens against.
 */
export interface OrganisationClientConfig {
  /** The authority whose discovery document names every endpoint. */
  authority: string;
  /** The public client identifier registered with the authority. */
  clientId: string;
  /** The exact SPA callback URI registered with the authority. */
  redirectUri: string;
  /** The registered post-logout URI, or `null` when none is registered. */
  postLogoutRedirectUri: string | null;
  /** The scopes to request: the configured organisation scopes plus the standard OIDC scope. */
  scopes: string[];
}

/**
 * The verification object every auth response carries (TASK-0062).
 *
 * Never carries the code, and never the unmasked destination: the masked `destination` is the most a browser may learn about an
 * identifier the caller already owns.
 */
export interface VerificationState {
  channel: VerificationChannel;
  state: VerificationStateKind;
  isVerified: boolean;
  verifiedAtUtc: string | null;
  method: IdentifierVerificationMethod | null;
  policyVersion: number;
  destination: string | null;
  expiresAtUtc: string | null;
  attemptsRemaining: number | null;
  allowsCode: boolean;
  allowsLink: boolean;
  resendAvailableAtUtc: string | null;
}

/** Outcome of `POST /auth/verify` (`VerifyIdentifierResult`). */
export interface VerifyCodeResult {
  userId: string;
  /** Channel that was settled. */
  channel: VerificationChannel;
  /** Instant the identifier was proved. */
  verifiedAtUtc: string;
  /** `true` when the identifier had already been verified, so the call changed nothing (and is still a success). */
  alreadyVerified: boolean;
}

/** A provider identity attached to the account, as its owner sees it (TASK-0062). */
export interface ExternalIdentity {
  id: string;
  provider: string;
  providerEmail: string | null;
  linkedAtUtc: string;
  /** `provider-sign-in` or `owner-grant`. */
  linkedBy: string;
  lastLoginAtUtc: string | null;
  /** Computed by the server: whether unlinking would leave another way in. */
  canUnlink: boolean;
}

/** Outcome of a password reset. */
export interface ResetPasswordResult {
  userId: string;
  revokedSessions: number;
}

/** One active refresh-token family, as shown by the "active sessions" screen. */
export interface SessionInfo {
  familyId: string;
  client: string;
  issuedAtUtc: string;
  expiresAtUtc: string;
  isCurrent: boolean;
}

/** Outcome of revoking a device session. */
export interface RevokeSessionResult {
  familyId: string;
  revokedTokens: number;
  wasCurrentSession: boolean;
}

/** Outcome of a password change. */
export interface ChangePasswordResult {
  userId: string;
  revokedSessions: number;
  securityStampRotated: boolean;
}

/** One model offered by a provider (`AiModelOptionDto`). */
export interface AiModelOption {
  id: string;
  displayName: string;
  contextWindow: number;
  maxOutputTokens: number;
  supportsStreaming: boolean;
  supportsFewShot: boolean;
}

/** One provider family of the catalog (`AiProviderOptionDto`). */
export interface AiProviderOption {
  kind: string;
  displayNameFa: string;
  displayNameEn: string;
  defaultBaseUrl: string | null;
  requiresApiKey: boolean;
  requiresBaseUrl: boolean;
  supportsModelProbe: boolean;
  allowsAnyModel: boolean;
  /** True when the catalog has no default model, so a credential of this provider must name one (TASK-0008). */
  requiresModel: boolean;
  models: AiModelOption[];
}

/** One field-level failure inside the `errors` extension of a problem document. */
export interface ApiProblemFieldError {
  field: string;
  code: string;
  message: string;
}

/**
 * RFC 7807 problem document as written by `ProblemDetailsFactoryAdapter`.
 *
 * `code` is the stable member the SPA switches on; `title` and `detail` are localized by `Accept-Language`.
 */
export interface ApiProblem {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  code: string;
  correlationId?: string;
  traceId?: string;
  errors?: ApiProblemFieldError[];
  retryAfterSeconds?: number;
  /** Provider or transport explanation of an AI failure, published by the exception middleware (TASK-0009). */
  upstreamDetail?: string;
}

/** Standard paged envelope used by every list endpoint (API surface §1). */
export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/**
 * Ordered steps of the guided wizard (`WizardStep`, ADR-0009).
 *
 * The target model comes first because the *server* numbers it `0` (`WizardStep.TargetModel`): each step's answer is
 * persisted under a key derived from that number (`1_domain` … `8_review`), so the new step was numbered `0` to leave
 * every stored key where it was. The order here is the order of the stepper, nothing more.
 */
export const WIZARD_STEPS = [
  'TargetModel',
  'Domain',
  'TaskType',
  'TechStack',
  'Context',
  'Persona',
  'OutputFormat',
  'Level',
  'Review',
] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/**
 * Family of a target model, which is the prompt framework the model is authored against (`TargetModelFamily`).
 *
 * The family is what the resolver picks the guidance from, so it travels as an explicit configuration choice rather than
 * being inferred from the model's name (Appendix A §6).
 */
export const TARGET_MODEL_FAMILIES = ['Gpt', 'Claude', 'Gemini', 'LocalLlm', 'Public'] as const;
export type TargetModelFamily = (typeof TARGET_MODEL_FAMILIES)[number];

/** One model of the target-model registry (`TargetModelDto`, API surface §3.13). */
export interface TargetModel {
  /** Stable lowercase slug persisted on the prompt and sent back on selection, for example `claude`. */
  id: string;
  /** Label of the selector; model names are proper nouns, so it stays Latin in both locales. */
  displayName: string;
  family: TargetModelFamily;
  /** Whether the model may still be chosen for new generation. */
  isEnabled: boolean;
  /** Whether the model may be chosen for a *new* prompt; equal to {@link isEnabled} today and kept apart so a future rule (quota, plan) does not change the wire shape. */
  isSelectable: boolean;
}

/** The whole registry beside the selection policy of the product (`TargetModelCatalogueDto`). */
export interface TargetModelCatalogue {
  /** Every registered model, in registry order, including the disabled ones. */
  models: TargetModel[];
  /** Model a new prompt is pre-selected with, or `null` when the user must choose one explicitly (Appendix A §10). */
  defaultTargetModelId: string | null;
}

/** Lifecycle state of a draft (`WizardSessionStatus`). */
export const WIZARD_SESSION_STATUSES = ['InProgress', 'Completed', 'Abandoned'] as const;
export type WizardSessionStatus = (typeof WIZARD_SESSION_STATUSES)[number];

/** Problem space of a prompt — the options of wizard step 1 (`PromptDomain`). */
export const PROMPT_DOMAINS = [
  'Programming',
  'DataScience',
  'DevOps',
  'Security',
  'UiUx',
  'Business',
  'Writing',
  'Research',
  'Education',
  'Marketing',
  'Legal',
  'Medical',
  'Custom',
] as const;
export type PromptDomain = (typeof PROMPT_DOMAINS)[number];

/** Kind of work the prompt asks for — the options of wizard step 2 (`TaskType`). */
export const TASK_TYPES = [
  'CodeGeneration',
  'Debugging',
  'Refactoring',
  'CodeReview',
  'UnitTesting',
  'ArchitectureDesign',
  'Documentation',
  'Explanation',
  'Optimization',
  'Translation',
  'Custom',
] as const;
export type TaskType = (typeof TASK_TYPES)[number];

/** Requested response shape — the options of wizard step 6 (`OutputFormat`). */
export const OUTPUT_FORMATS = [
  'Markdown',
  'Json',
  'Yaml',
  'CodeOnly',
  'Table',
  'Diagram',
  'PlainText',
] as const;
export type OutputFormat = (typeof OUTPUT_FORMATS)[number];

/** Language of the *generated prompt*, independent of the interface language (`PromptLocale`). */
export const PROMPT_LOCALES = ['FaIR', 'En'] as const;
export type PromptLocale = (typeof PROMPT_LOCALES)[number];

/** Lifecycle state a completed prompt is saved with (`PromptStatus`). */
export const PROMPT_STATUSES = ['Draft', 'Final', 'Archived'] as const;
export type PromptStatus = (typeof PROMPT_STATUSES)[number];

/** Section document keys the renderer publishes (`PromptSection.ToDocumentKey`). */
export const PROMPT_SECTIONS = [
  'role',
  'context',
  'constraints',
  'task',
  'examples',
  'reasoningGuidance',
  'edgeCases',
  'outputFormat',
  'evaluationCriteria',
] as const;
export type PromptSection = (typeof PROMPT_SECTIONS)[number];

/** Everything the wizard UI needs to render one step (`WizardStepDescriptor`). */
export interface WizardStepDescriptor {
  step: WizardStep;
  /** Answer-map key the step is persisted under, for example `1_domain`. */
  key: string;
  titleFa: string;
  titleEn: string;
  hintFa: string;
  hintEn: string;
  allowsMultiple: boolean;
  allowsCustomInput: boolean;
  isRequired: boolean;
  /** Predefined option tokens in presentation order; the SPA resolves their labels. */
  options: string[];
}

/**
 * Kind of step the framework-driven wizard renders (`FrameworkWizardStepKind`, TASK-0012 §3–§7, TASK-0013 §2.3).
 *
 * The kind is what tells the screen *how* to render a step rather than *what* it is: a framework section is a prose
 * field, the custom-sections step is a list editor and the review step is the whole document. Nothing about the
 * sections themselves is enumerated here — their number, keys and headings come from the framework file.
 *
 * `Domain` is the one step the product adds to the framework's own sections (TASK-0013 AC-04): it sits between the pair and
 * the framework's sections and scopes the options those sections offer.
 */
export const FRAMEWORK_STEP_KINDS = [
  'Selection',
  'Domain',
  'FrameworkSection',
  'CustomSections',
  'Review',
] as const;
export type FrameworkStepKind = (typeof FRAMEWORK_STEP_KINDS)[number];

/**
 * One step of the framework-driven wizard (`FrameworkStepDto`, TASK-0012 §3, §4, §7; TASK-0013 §2.4).
 *
 * `key` is the key the step's answer is submitted under, and for a framework section it is derived from the file's own
 * heading — so the steps on screen and the keys of the answers cannot disagree.
 */
export interface FrameworkStep {
  /** Stable key the answer is submitted under, for example `output-format`. */
  key: string;
  /** Heading of the step: the framework's own heading, shown verbatim. */
  title: string;
  /** Persian heading for the steps the product owns; `null` for a framework section. */
  titleFa: string | null;
  kind: FrameworkStepKind;
  /** One-based position in the wizard. */
  order: number;
  /** Whether the framework marks the step optional, so the user may skip it. */
  isOptional: boolean;
  /** The section's authored body, shown as the step's guidance; empty for the product's own steps. */
  guidance: string;
  /**
   * Catalogue step whose options this step offers, or `null` for a step that is free text only (TASK-0013 §2.4).
   *
   * It is the *persisted* catalogue key (`2_task`), so the screen asks the very endpoint the draft wizard asks and the
   * options are the ones the catalogue already holds — never a second list.
   */
  optionStepKey: string | null;
  /** Built-in tokens of `optionStepKey`, used as the picker's fallback when the catalogue has no options for it yet. */
  options: string[];
  /** Whether more than one option may be selected, as the catalogue step states. */
  allowsMultiple: boolean;
  /** Whether the user may answer with free text next to the options (TASK-0013 AC-07). */
  allowsCustomInput: boolean;
}

/** The wizard of one `(target model, prompt level)` pair (`FrameworkStepsDto`, TASK-0012 §2.2). */
export interface FrameworkSteps {
  targetModelId: string | null;
  level: PromptLevel;
  /** Allowlisted template the framework file belongs to. */
  templateId: string | null;
  /** Framework file the steps were read from, for example `gpt-standard.md`. */
  fileName: string | null;
  /** Whether a more specific framework was skipped in favour of this one. */
  usedFallback: boolean;
  /** Persian warning to show when a fallback was used. */
  fallbackWarningFa: string | null;
  steps: FrameworkStep[];
  /** One line per candidate that was skipped while resolving the pair. */
  diagnostics: string[];
}

/** One section of a generated prompt (`FrameworkPromptSectionDto`, TASK-0012 §8). */
export interface FrameworkPromptSection {
  key: string;
  title: string;
  content: string;
  /** Whether the section came from the framework file rather than from the user. */
  isFromFramework: boolean;
  /** Whether the user supplied the section's content. */
  isFilled: boolean;
}

/** The prompt a framework and a set of answers produce (`FrameworkPromptDto`, TASK-0012 §8). */
export interface FrameworkPrompt {
  targetModelId: string | null;
  level: PromptLevel;
  templateId: string | null;
  fileName: string | null;
  /** Rendered prompt, ready to paste into a model. */
  markdown: string;
  sections: FrameworkPromptSection[];
  /** Sections the framework requires and the user has not answered yet. */
  unfilledSections: FrameworkPromptSection[];
  /**
   * Identifiers of the prompt patterns the prompt was generated with, in the order their instructions were appended
   * (TASK-0039). Empty when no pattern was selected.
   */
  patternIds: string[];
}

/** Sort of a curated step's option list (`WizardOptionSort`, TASK-0002). */
export const WIZARD_OPTION_SORTS = ['Relevance', 'CommunityUsage', 'MyUsage'] as const;
export type WizardOptionSort = (typeof WIZARD_OPTION_SORTS)[number];

/**
 * One ranked option of a curated wizard step (`WizardOptionDto`).
 *
 * The DTO carries the token and the two usage counters and nothing else: the SPA resolves the label from its own
 * message catalogue (`wizard.options.<token>`), so an option added to the catalogue cannot ship an untranslated label.
 */
export interface WizardOptionItem {
  /** Stable token stored in the answers; the chip label is looked up from it, never sent. */
  token: string;
  /** How often every user together has picked the option. */
  usageCount: number;
  /** How often the signed-in user has picked it. */
  myUsageCount: number;
}

/** One step's ranked options (`WizardOptionPageDto`). */
export interface WizardOptionPage {
  /** Document key of the step the page belongs to, for example `3_stack`. */
  stepKey: string;
  /** Whether the step carries a catalogue at all; `false` makes the panel keep its static chips. */
  isCurated: boolean;
  items: WizardOptionItem[];
  /** How many options matched the domain scope and the search, ahead of the visible cap. */
  totalMatching: number;
  /** Whether `items` was cut at the visible cap. */
  isTruncated: boolean;
}

/** One step's answer (`WizardAnswerDto`). */
export interface WizardAnswer {
  selected: string[];
  customText: string | null;
  constraints: Record<string, string>;
}

/**
 * One section the user authored in a draft (`WizardCustomSectionDto`, TASK-0010 §11).
 *
 * A draft's sections are all custom: the schema of a prompt is resolved when the draft is *completed*, so at this point
 * there is nothing for a section to be standard against. What a draft section carries is therefore an identifier, a
 * heading and a body — no schema key, no origin and no activation flag, which is also why the edit vocabulary of a draft
 * stops short of activation.
 */
export interface WizardCustomSection {
  /** Identifier the application assigned; stable for the life of the section. */
  id: string;
  /** Heading of the section. */
  title: string;
  /** Body of the section; may be empty while it is still being written. */
  content: string;
}

/** A draft with everything the wizard screen needs to restore itself (`WizardSessionDto`). */
export interface WizardSession {
  id: string;
  status: WizardSessionStatus;
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  skippedSteps: WizardStep[];
  /** Required steps that are neither answered nor skipped. */
  missingSteps: WizardStep[];
  promptLevel: PromptLevel;
  locale: PromptLocale;
  /** Answers keyed by their persisted document key, for example `1_domain`. */
  answers: Record<string, WizardAnswer>;
  forkedFromTemplateId: string | null;
  /**
   * Sections the user authored in this draft, in the order they put them in (`WizardCustomSectionDto`).
   *
   * They are the wizard's own affordance for a section the schema does not declare (TASK-0010 §11): they are written by
   * `POST /wizard/sessions/{id}/sections`, they take part in every render of the draft, and they become sections of the
   * prompt the draft completes into.
   */
  customSections: WizardCustomSection[];
  completedPromptId: string | null;
  /** Concurrency token echoed back as `If-Match` on the next write. */
  revision: number;
  draftMarkdown: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  expiresAtUtc: string;
}

/** Answer to an auto-save: the new revision and the preview that write produced (`WizardDraftDto`). */
export interface WizardDraft {
  revision: number;
  draftMarkdown: string;
  updatedAtUtc: string;
}

/** One actionable improvement of the deterministic quality checklist (`WizardQualityHintDto`). */
export interface WizardQualityHint {
  /** Stable token such as `MissingSection`. */
  kind: string;
  /** Affected section, when the finding is section-scoped. */
  sectionKey: string | null;
  messageFa: string;
  messageEn: string;
}

/** A rendered preview plus its deterministic quality report (`WizardPreviewDto`). */
export interface WizardPreview {
  markdown: string;
  level: PromptLevel;
  locale: PromptLocale;
  presentSections: string[];
  missingSections: string[];
  tokensEstimate: number;
  qualityScore: number;
  isSufficient: boolean;
  hints: WizardQualityHint[];
}

/** Lightweight projection of a draft, used by the "continue where you left off" rail (`WizardSessionSummaryDto`). */
export interface WizardSessionSummary {
  id: string;
  status: WizardSessionStatus;
  currentStep: WizardStep;
  promptLevel: PromptLevel;
  locale: PromptLocale;
  completedSteps: WizardStep[];
  missingSteps: WizardStep[];
  revision: number;
  updatedAtUtc: string;
  expiresAtUtc: string;
  completedPromptId: string | null;
}

/**
 * A framework draft with everything the framework wizard needs to restore itself (`FrameworkSessionDto`, ADR-0018 §4).
 *
 * Deliberately not `WizardSession`: that shape's step fields are all `WizardStep`s, and a framework draft's step keys are
 * the section keys of a framework file, which no `WizardStep` can express.
 */
export interface FrameworkSession {
  id: string;
  status: WizardSessionStatus;
  /** Model the draft is written for, once the selection step was answered. */
  targetModelId: string | null;
  promptLevel: PromptLevel;
  locale: PromptLocale;
  /** Step the user is on: a framework section key, or one of the wizard's own step keys. */
  stepKey: string | null;
  /** Answers keyed by step key — a section key for a section, a reserved key for the wizard's own steps. */
  answers: Record<string, WizardAnswer>;
  /** Steps the user deliberately skipped, in the order they were skipped. */
  skippedStepKeys: string[];
  /**
   * Prompt patterns the user selected, in the order they were stored (TASK-0039). Empty for a draft written before the
   * pattern axis existed, which is exactly the behaviour such a draft had.
   */
  patternIds: string[];
  customSections: WizardCustomSection[];
  /**
   * Template the draft was forked from, when the wizard was opened from the template library (TASK-0024).
   *
   * A fork applies the template without a target model — the selection step is where the user still chooses one — so the
   * origin is carried here and in the draft's own sections rather than in a resolved framework.
   */
  forkedFromTemplateId: string | null;
  completedPromptId: string | null;
  /** Concurrency token echoed back as `If-Match` on the next write. */
  revision: number;
  draftMarkdown: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  expiresAtUtc: string;
}

/** Lightweight projection of a framework draft, used by the resume rail (`FrameworkSessionSummaryDto`). */
export interface FrameworkSessionSummary {
  id: string;
  status: WizardSessionStatus;
  targetModelId: string | null;
  promptLevel: PromptLevel;
  locale: PromptLocale;
  stepKey: string | null;
  /** Number of steps that carry an answer. */
  answeredSteps: number;
  /** Number of steps the user deliberately skipped. */
  skippedSteps: number;
  revision: number;
  updatedAtUtc: string;
  expiresAtUtc: string;
  completedPromptId: string | null;
}

/** The prompt a completed wizard session produced (`CompletedWizardPromptDto`). */
export interface CompletedWizardPrompt {
  promptId: string;
  title: string;
  version: number;
  markdown: string;
  level: PromptLevel;
  locale: PromptLocale;
  status: PromptStatus;
  qualityScore: number;
  tokensEstimate: number;
  createdAtUtc: string;
  /**
   * Instant of the last write to the prompt this completion produced or refiled.
   *
   * Equal to `createdAtUtc` for a prompt that was just created, and later on the refile path (TASK-0031), where it is
   * the concurrency token the client holds next.
   */
  updatedAtUtc: string;
}

/** One piece of co-pilot advice (`AiSuggestionDto`, ADR-0016 §2). */
export interface AiSuggestion {
  /** Identifier echoed back when applying it. */
  id: string;
  /** Stable token such as `WordingImprovement`. */
  kind: string;
  targetSection: string | null;
  textFa: string;
  textEn: string | null;
  applied: boolean;
}

/** One failed fidelity check (`AiFidelityFindingDto`, ADR-0016 §3). */
export interface AiFidelityFinding {
  kind: string;
  sectionKey: string | null;
  messageFa: string;
  messageEn: string;
}

/** Fidelity report of one candidate (`AiFidelityDto`). */
export interface AiFidelity {
  /** Either `Preserved`, `Rejected` or `NotApplicable`. */
  status: string;
  score: number;
  findings: AiFidelityFinding[];
}

/** Normalized token accounting of one call (`AiUsageDto`). */
export interface AiUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  isEstimated: boolean;
}

/** Answer of a non-streaming co-pilot call (`AiAnswerDto`). */
export interface AiAnswer {
  interactionId: string;
  providerKind: string;
  modelId: string;
  /** Answer text with the suggestion block stripped. */
  text: string;
  suggestions: AiSuggestion[];
  fidelity: AiFidelity | null;
  usage: AiUsage;
  latencyMs: number;
  outcome: string;
  errorCode: string | null;
  errorMessageFa: string | null;
  /** Provider or transport explanation of a failure, or `null` (TASK-0009). */
  errorDetail?: string | null;
}

/** One recorded co-pilot call (`AiInteractionDto`); the apply endpoint answers with it. */
export interface AiInteraction {
  id: string;
  purpose: string;
  providerKind: string;
  modelId: string;
  promptId: string | null;
  wizardSessionId: string | null;
  promptLevel: string;
  requestPreview: string | null;
  responsePreview: string | null;
  suggestions: AiSuggestion[];
  usage: AiUsage;
  latencyMs: number;
  outcome: string | null;
  errorCode: string | null;
  streamed: boolean;
  createdAtUtc: string;
}

/** Why a new prompt version was appended (`PromptVersionOrigin`, ADR-0010). */
export const PROMPT_VERSION_ORIGINS = [
  'Wizard',
  'ManualEdit',
  'AiRefine',
  'Restore',
  'Duplicate',
  'AiGenerated',
  'StoreCopy',
] as const;
export type PromptVersionOrigin = (typeof PROMPT_VERSION_ORIGINS)[number];

/** Download formats offered by `GET /prompts/{id}/export` (`PromptExportFormat`). */
export const PROMPT_EXPORT_FORMATS = ['Markdown', 'PlainText', 'Json'] as const;
export type PromptExportFormat = (typeof PROMPT_EXPORT_FORMATS)[number];

/**
 * Reachability of a share link (`ShareScope`).
 *
 * `PrivateExpiring` requires an expiry instant; the API answers `400 validation_error` when it is missing, so the SPA
 * enforces the same rule before sending.
 */
export const SHARE_SCOPES = ['Public', 'PrivateExpiring'] as const;
export type ShareScope = (typeof SHARE_SCOPES)[number];

/** Review state of an admin-curated template (`TemplateStatus`). */
export const TEMPLATE_STATUSES = ['Draft', 'InReview', 'Published', 'Archived'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

/** Who may see a template (`TemplateVisibility`). */
export const TEMPLATE_VISIBILITIES = ['Public', 'Private'] as const;
export type TemplateVisibility = (typeof TEMPLATE_VISIBILITIES)[number];

/** Input kind rendered for a template variable (`TemplateVariableType`). */
export const TEMPLATE_VARIABLE_TYPES = [
  'Text',
  'LongText',
  'SingleSelect',
  'MultiSelect',
  'Number',
  'Boolean',
] as const;
export type TemplateVariableType = (typeof TEMPLATE_VARIABLE_TYPES)[number];

/** One section marker of a prompt body (`PromptSectionMarkerDto`). */
export interface PromptSectionMarker {
  /** Persisted document key, for example `outputFormat`. */
  key: string;
  section: PromptSection;
  isPresent: boolean;
}

/** The template a prompt descends from (`ForkedFromTemplateDto`). */
export interface ForkedFromTemplate {
  templateId: string;
  slug: string;
  version: number;
  nameFa: string;
  nameEn: string;
}

/** The card shape of a prompt (`PromptSummaryDto`) — what the library grid renders. */
export interface PromptSummary {
  id: string;
  title: string;
  status: PromptStatus;
  promptLevel: PromptLevel;
  /**
   * Domains of the prompt, in the order its answers carried them (TASK-0046 §3).
   *
   * A list rather than a single value: a prompt belongs to every category it names, and the library's `?domain=` filter
   * matches a prompt carrying the value anywhere in this list. Empty — never `null` — when nothing classified it.
   */
  domains: PromptDomain[];
  /** Task types of the prompt, in the order its answers carried them; empty when nothing classified it. */
  taskTypes: TaskType[];
  /**
   * Intended audience the author stated, or `null` when none was stated (TASK-0051).
   *
   * The prompt owns the field: the wizard derives an initial value from its Context answers, and the library's identity
   * section is the only place it is edited. The Prompt Store package presents this value rather than keeping a copy.
   */
  audience: string | null;
  /**
   * Slug derived from the title.
   *
   * It follows the title while the prompt has never been published to the Prompt Store, and is frozen from the first
   * publication on (TASK-0046 §9.1 Q1). Nothing in this release resolves a prompt by slug; it is identity data that seeds
   * the store's own address.
   */
  slug: string;
  locale: PromptLocale;
  /** First characters of the body as plain text, for the preview line. */
  excerpt: string;
  qualityScore: number;
  tokensEstimate: number;
  tags: string[];
  categoryId: string | null;
  /**
   * Taxonomy subcategory the prompt is filed under (TASK-0053), or `null` for a prompt filed by category only.
   * When it is set, `categoryId` is always this row's parent.
   */
  subCategoryId: string | null;
  isFavorite: boolean;
  isPublic: boolean;
  currentVersion: number;
  versionCount: number;
  hasShareLink: boolean;
  viewCount: number;
  exportCount: number;
  duplicateCount: number;
  createdAtUtc: string;
  /** Doubles as the optimistic-concurrency token sent back as `If-Match`. */
  updatedAtUtc: string;
  lastExportedAtUtc: string | null;
  isDeleted: boolean;
  deletedAtUtc: string | null;
}

/** One search result: the card plus its relevance (`PromptSearchHitDto`). */
export interface PromptSearchHit {
  prompt: PromptSummary;
  /** `null` for a filter-only search; a null score is not "no match". */
  score: number | null;
}

/** A page of prompt results plus the facet counts of the sidebar (`PromptSearchPageDto`). */
export interface PromptSearchPage {
  page: PagedResult<PromptSearchHit>;
  /** Matches per domain, computed over the same filtered set as the page. */
  domainCounts: Partial<Record<PromptDomain, number>>;
  /** Matches per depth, computed over the same filtered set as the page. */
  levelCounts: Partial<Record<PromptLevel, number>>;
  favoriteCount: number;
}

/** Whether a structured section belongs to the resolved schema or was authored by the user (`PromptSectionOrigin`). */
export const PROMPT_SECTION_ORIGINS = ['Standard', 'Custom'] as const;
export type PromptSectionOrigin = (typeof PROMPT_SECTION_ORIGINS)[number];

/**
 * One explicit change to the structured form of a prompt (`PromptSectionOperation`).
 *
 * The vocabulary is operational rather than a whole-document replacement, which is what makes "an absent section" mean
 * "leave it alone" instead of "delete it".
 */
export const PROMPT_SECTION_OPERATIONS = [
  'AddCustom',
  'RemoveCustom',
  'SetContent',
  'SetTitle',
  'SetActive',
  'Move',
] as const;
export type PromptSectionOperation = (typeof PROMPT_SECTION_OPERATIONS)[number];

/** One section of a structured prompt (`PromptSectionDto`). */
export interface PromptStructureSection {
  /** Stable identifier inside its prompt; assigned by the application, never by the client. */
  id: string;
  /** Schema key of a standard section; `null` for a custom one. */
  key: string | null;
  title: string;
  content: string;
  origin: PromptSectionOrigin;
  /** One-based position inside the prompt. */
  order: number;
  /** Whether the section takes part in the current rendering. */
  isActive: boolean;
}

/**
 * The structured form of a prompt (`PromptStructureDto`).
 *
 * Present only for a prompt that was produced by the target-model axis; a hand-written, imported or pre-axis prompt
 * answers `null` and is edited as a document instead.
 */
export interface PromptStructure {
  targetModelId: string;
  promptLevel: PromptLevel;
  templateId: string;
  templateVersion: string | null;
  schemaVersion: number;
  sections: PromptStructureSection[];
  /** Markdown snapshot the structure rendered, or `null` when none was taken. */
  renderedPrompt: string | null;
  /** Keys the schema mandates and the structure does not carry, in schema order. */
  missingRequiredKeys: string[];
  /** `false` when the pair no longer resolves, which is why the schema-derived members are empty. */
  schemaAvailable: boolean;
}

/**
 * What a proposed merge would do with one section (`PromptMergeChangeDto.Kind`).
 *
 * The vocabulary is the merge's own, and it is a vocabulary of *decisions* rather than of edits: `fill` and `update` are
 * already part of the preview, `addCustom` was kept as a custom section, `clear` was held back for the user to decide on,
 * and `ignored` was considered and not applied.
 */
export const PROMPT_MERGE_CHANGE_KINDS = [
  'fill',
  'update',
  'clear',
  'addCustom',
  'ignored',
] as const;
export type PromptMergeChangeKind = (typeof PROMPT_MERGE_CHANGE_KINDS)[number];

/**
 * One entry of the review list a proposed merge produces (`PromptMergeChangeDto`).
 *
 * The content the prompt holds travels with the proposal on purpose: the review screen can show what would be replaced
 * without keeping a copy of the prompt of its own.
 */
export interface PromptMergeChange {
  kind: PromptMergeChangeKind;
  /** Schema key the proposal named, when it named one. */
  key: string | null;
  /** Heading involved, when one is known. */
  title: string | null;
  /** Identifier of the section in the merged structure, when the change was applied. */
  sectionId: string | null;
  /** Content the prompt held before the merge, when it held any. */
  currentContent: string | null;
  /** Content that was proposed. */
  proposedContent: string | null;
  /** Whether the change is already part of `preview`. */
  wasApplied: boolean;
}

/** One note the reading of an answer produced (`PromptMergeWarningDto`). */
export interface PromptMergeWarning {
  /** Stable code of the note, for example `duplicate_heading`. */
  code: string;
  /** What the parser had to decide, in the answer's own terms. */
  detail: string;
  /** Persian message the review screen shows as it is. */
  message: string;
}

/**
 * The outcome of proposing a merge, with nothing written (`PromptMergeProposalDto`).
 *
 * Nothing in this answer has been stored: the prompt, its body and its history are untouched, and the caller accepts what
 * it wants through `PUT /prompts/{id}/structure`.
 */
export interface PromptMergeProposal {
  promptId: string;
  /** Every proposal the merge considered, in the order it considered them. */
  changes: PromptMergeChange[];
  /** Standard keys the schema does not declare, which were never trusted as standard fields. */
  unknownKeys: string[];
  /** Whether a proposal was held back for the user to decide on. */
  requiresReview: boolean;
  /** Whether the answer could be read without a single note. */
  isExact: boolean;
  /** What the reading had to preserve, when it had to preserve anything. */
  warnings: PromptMergeWarning[];
  /** The structure as it would stand if the applied changes were accepted. */
  preview: PromptStructure;
}

/** A prompt in full (`PromptDetailDto`). */
export interface PromptDetail {
  summary: PromptSummary;
  markdown: string;
  sections: PromptSectionMarker[];
  missingSections: string[];
  wizardSessionId: string | null;
  forkedFromTemplateRef: ForkedFromTemplate | null;
  shareLinkId: string | null;
  aiInteractionIds: string[];
  /** Structured form, or `null` for a prompt the axis did not produce (`PromptDetailDto.Structure`). */
  structure: PromptStructure | null;
  /**
   * Whether the Prompt Store has frozen this prompt, and whether this caller may still write it (ADR-0023).
   * Absent only on a response that predates the lock.
   */
  approvalLock?: PromptApprovalLock | null;
  /**
   * The prompt's store packages, projected **for administrators only** (ADR-0023, BQ-3). `null` for every other
   * caller, including the owner, so the field is absent rather than hidden.
   */
  storeProvenance?: PromptStoreProvenance[] | null;
}

/** The approval lock of a saved prompt as the client reads it (`PromptApprovalLockDto`). */
export interface PromptApprovalLock {
  /** Whether the owner may no longer write the prompt. */
  isLocked: boolean;
  /** Whether the lock comes from an approval rather than a pending review. */
  isPublished: boolean;
  /** Instant the lock began, or `null`. */
  lockedAtUtc: string | null;
  /** Whether this caller may still write it: true on an unlocked prompt, and true for an administrator. */
  isWritableByCaller: boolean;
}

/** One store package of a prompt, as an administrator sees it (`PromptStoreProvenanceDto`). */
export interface PromptStoreProvenance {
  publicationId: string;
  status: PublicationStatus;
  slug: string;
  titleFa: string;
  revision: number;
  submittedAtUtc: string | null;
  publishedAtUtc: string | null;
}

/** One entry of a prompt's version history (`PromptVersionSummaryDto`). */
export interface PromptVersionSummary {
  version: number;
  changeSummary: string | null;
  origin: PromptVersionOrigin;
  promptLevel: PromptLevel;
  createdByUserId: string;
  createdAtUtc: string;
  contentHash: string;
  /** False once the snapshot fell outside the retention window and was compacted. */
  bodyRetained: boolean;
  restoredFromVersion: number | null;
  addedSections: string[];
  removedSections: string[];
}

/** One snapshot of a prompt in full (`PromptVersionDto`). */
export interface PromptVersion {
  summary: PromptVersionSummary;
  /** `null` when the body was compacted away; the snapshot still exists. */
  markdown: string | null;
  bodyRetained: boolean;
  /** Answers the version was rendered from, keyed by their persisted document key. */
  answers: Record<string, string>;
}

/** Line-level difference between two snapshots (`PromptVersionDiffDto`). */
export interface PromptVersionDiff {
  promptId: string;
  fromVersion: number;
  toVersion: number;
  /** Unified diff with a `---`/`+++` header; empty when the bodies are byte-identical. */
  unifiedDiff: string;
  addedLines: number;
  removedLines: number;
  isIdentical: boolean;
  isTruncated: boolean;
  fromChangeSummary: string | null;
  toChangeSummary: string | null;
  fromCreatedAtUtc: string;
  toCreatedAtUtc: string;
}

/** Result of toggling the favorite flag (`PromptFavoriteDto`). */
export interface PromptFavoriteResult {
  promptId: string;
  isFavorite: boolean;
}

/** A prompt rendered into a downloadable file (`PromptExportDto`). */
export interface PromptExport {
  fileName: string;
  contentType: string;
  content: string;
  format: PromptExportFormat;
}

/** A share link as its owner sees it (`ShareLinkDto`); the password is never returned. */
export interface ShareLink {
  id: string;
  /** Token that forms the public address, stored in the clear by design. */
  token: string;
  scope: ShareScope;
  promptId: string;
  promptVersion: number;
  requiresPassword: boolean;
  maxViews: number | null;
  viewCount: number;
  remainingViews: number | null;
  allowIndexing: boolean;
  isViewable: boolean;
  expiresAtUtc: string | null;
  revokedAtUtc: string | null;
  createdAtUtc: string;
  lastAccessedAtUtc: string | null;
}

/** Whether a prompt is currently shared, and through which link (`PromptShareStateDto`). */
export interface PromptShareState {
  promptId: string;
  /** `null` when the prompt is not shared — an ordinary answer, not an error. */
  link: ShareLink | null;
}

/** Outcome of revoking a prompt's sharing (`RevokedShareLinksDto`). */
export interface RevokedShareLinks {
  promptId: string;
  revokedCount: number;
  revokedLinkIds: string[];
}

/** The card shape of a template (`TemplateSummaryDto`) — what the catalogue grid renders. */
export interface TemplateSummary {
  id: string;
  slug: string;
  titleFa: string;
  titleEn: string;
  descriptionFa: string;
  descriptionEn: string;
  domain: PromptDomain | null;
  taskType: TaskType | null;
  iconName: string | null;
  status: TemplateStatus;
  visibility: TemplateVisibility;
  tags: string[];
  categoryId: string | null;
  isFeatured: boolean;
  currentVersion: number;
  /** Levels the template offers a body for, in wizard order. */
  supportedLevels: PromptLevel[];
  defaultPromptLevel: PromptLevel;
  forkCount: number;
  viewCount: number;
  /** `null` while nobody rated the template. */
  averageRating: number | null;
  ratingCount: number;
  /** Doubles as the optimistic-concurrency token sent back as `If-Match`. */
  updatedAtUtc: string;
  publishedAtUtc: string | null;
}

/** One declared template variable (`TemplateVariableDto`): an extra field a fork adds. */
export interface TemplateVariable {
  key: string;
  labelFa: string;
  labelEn: string;
  type: TemplateVariableType;
  isRequired: boolean;
  options: string[];
  defaultValue: string[];
  example: string | null;
}

/** The full shape of a template (`TemplateDetailDto`). */
export interface TemplateDetail {
  summary: TemplateSummary;
  /** Simple-level body, or `null` when the level is unsupported or unauthored. */
  bodySimple: string | null;
  bodyStandard: string | null;
  bodyExpert: string | null;
  variables: TemplateVariable[];
  /** Answers a fork starts with, keyed by wizard step. */
  prefilledAnswers: Partial<Record<WizardStep, WizardAnswer>>;
}

/** A page of templates plus the facet counts of the browser sidebar (`TemplateSearchPageDto`). */
export interface TemplateSearchPage {
  page: PagedResult<TemplateSummary>;
  domainCounts: Partial<Record<PromptDomain, number>>;
  featuredCount: number;
}

/** One entry of a template's version history (`TemplateVersionSummaryDto`); admin-only on the API side. */
export interface TemplateVersionSummary {
  version: number;
  changeSummary: string | null;
  createdByUserId: string;
  createdAtUtc: string;
  status: TemplateStatus;
  bodySimple: string | null;
  bodyStandard: string | null;
  bodyExpert: string | null;
}

/** The answer to a rating (`TemplateRatingDto`). */
export interface TemplateRatingResult {
  templateId: string;
  score: number;
  averageRating: number | null;
  ratingCount: number;
  forkCount: number;
  viewCount: number;
}

/**
 * Which source a generation reads when both are present (Appendix A §15).
 *
 * `Unspecified` is not "pick one for me": it is what a request carrying both sources says, and the API answers it with
 * `400 generation_source_ambiguous` rather than choosing. `Both` is the explicit confirmation to combine them, which is
 * why the screen shows it as its own option rather than as a combination of the other two.
 */
export const TEXT_SOURCE_CHOICES = ['Unspecified', 'Text', 'File', 'Both'] as const;
export type TextSourceChoice = (typeof TEXT_SOURCE_CHOICES)[number];

/** The two wire values of the generation contract (Appendix A §17). */
export const TEXT_TO_PROMPT_STATUSES = ['needs_clarification', 'completed'] as const;
export type TextToPromptStatus = (typeof TEXT_TO_PROMPT_STATUSES)[number];

/** One section of a generated prompt (`TextToPromptSectionDto`). */
export interface GeneratedPromptSection {
  /** Identifier the server assigned; a standard section's identifier is its schema key. */
  id: string;
  /** Either `standard` or `custom`. */
  type: string;
  /** Stable schema key of a standard section, or `null` for a custom one. */
  key: string | null;
  /** Heading, taken from the schema for a standard section. */
  title: string;
  content: string;
  /** One-based position in the rendering. */
  order: number;
  active: boolean;
}

/** One note about content a generation preserved rather than mapped (`TextToPromptWarningDto`). */
export interface GeneratedPromptWarning {
  /** Stable code, for example `unknown_heading_preserved`. */
  code: string;
  /** Sanitized diagnostic naming the key involved. */
  detail: string;
  /** Persian sentence the API ships beside the code; shown when the interface is Persian. */
  messageFa: string;
}

/**
 * Answer of `POST /ai/generate` (`TextToPromptResultDto`, Appendix A §17–§18).
 *
 * One of two states and never both: `questions` is filled for a clarification answer, `sections` and `markdown` for a
 * completed one. Nothing is stored by the call, so the result is a proposal the screen renders and the user keeps.
 */
export interface TextToPromptResult {
  status: TextToPromptStatus;
  /** Token describing the context this answer belongs to; echoed back by a follow-up call. */
  contextToken: string;
  targetModelId: string;
  promptLevel: PromptLevel;
  templateId: string;
  schemaVersion: number;
  /** Questions an incomplete source raised; empty for a completed answer. */
  questions: string[];
  /** Sections of a completed answer; empty for a clarification one. */
  sections: GeneratedPromptSection[];
  /** Standard keys the answer used that the schema does not declare. */
  unknownKeys: string[];
  warnings: GeneratedPromptWarning[];
  /** Rendered snapshot of a completed answer, or `null`. */
  markdown: string | null;
  interactionId: string;
  providerKind: string;
  modelId: string;
  usage: AiUsage;
  latencyMs: number;
}

/** Text extracted from an uploaded file (`TextSourceExtractionDto`). */
export interface TextSourceExtraction {
  /** Name of the file the text came from. */
  fileName: string;
  /** Extracted, normalized text. */
  text: string;
  /** Number of characters extracted. */
  characters: number;
}

/** Why a Forge balance moved (`ForgeLedgerEntryKind`, TASK-0035). */
export const FORGE_LEDGER_ENTRY_KINDS = ['Signup', 'Charge', 'Adjustment', 'Refund'] as const;
export type ForgeLedgerEntryKind = (typeof FORGE_LEDGER_ENTRY_KINDS)[number];

/**
 * One movement of the Forge ledger (`ForgeLedgerEntryDto`, TASK-0035 §5.7).
 *
 * `amount` is signed — positive raised the balance, negative lowered it — and `balanceAfter` is the running balance the
 * movement produced, which may itself be negative. Both are integers in Forge units; money is never a float here.
 */
export interface ForgeLedgerEntry {
  id: string;
  kind: ForgeLedgerEntryKind;
  amount: number;
  balanceAfter: number;
  occurredAtUtc: string;
  /** Provider that served the call, or `null` for a grant or a correction. */
  providerKind: string | null;
  modelId: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  description: string | null;
}

/**
 * The Forge balance and lifetime usage of the signed-in account (`ForgeBalanceDto`, TASK-0035 §5.5–§5.7).
 *
 * A negative `balance` is data, not an error: the product's rule is that a spent balance never blocks the co-pilot, so
 * the panel reports the debt honestly and never disables an action because of it.
 */
export interface ForgeBalance {
  balance: number;
  /** Reward a new account is granted, so the panel can explain where the balance came from. */
  signupReward: number;
  currencyName: string;
  totalSpent: number;
  totalGranted: number;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  /** The most recent movements, so the panel needs one request rather than two. */
  recent: ForgeLedgerEntry[];
  /** Whether the account has spent more than it holds. */
  isNegative: boolean;
}

/** One page of the Forge ledger (`ForgeLedgerPageDto`, TASK-0035 §5.7). */
export interface ForgeLedgerPage {
  entries: ForgeLedgerEntry[];
  totalCount: number;
  skip: number;
  take: number;
}

/** One row of the Forge price table (`ForgePriceDto`, TASK-0035 §5.3, TASK-0036 §5). */
export interface ForgePrice {
  modelId: string;
  inputPerThousand: number;
  outputPerThousand: number;
  /**
   * Platform provider profile the price belongs to, or `null` for the global override that applies to every connection
   * (ADR-0019 §D5). A row without a profile behaves exactly as it did before profiles existed.
   */
  providerProfileId: string | null;
}

/**
 * The effective Forge pricing table (`ForgePricingDto`, TASK-0035 §5.3, §5.8).
 *
 * `models` is the administrator-set table the panel edits; `configuredModels` comes from deployment configuration and
 * is read-only — the two are published apart so a save can never copy a configured price into the stored table.
 */
export interface ForgePricing {
  signupReward: number;
  currencyName: string;
  models: ForgePrice[];
  configuredModels: ForgePrice[];
  defaultInputPerThousand: number;
  defaultOutputPerThousand: number;
  /** Profiles a price row may reference, so the editor offers a selector (ADR-0019 §5). */
  providerProfiles: SelectableProviderProfile[];
}

/** One platform provider profile as a user's picker reads it (`SelectableProviderProfileDto`, ADR-0019 §D3). */
export interface SelectableProviderProfile {
  id: string;
  displayName: string;
  description: string | null;
  providerKind: AiProviderKind;
  baseUrl: string;
  /** Model the profile serves, or `null`, so the picker can show what a profile is for. */
  modelId: string | null;
}

/** One platform provider profile as the administrator panel reads it (`ProviderProfileDto`, ADR-0019). */
export interface ProviderProfile {
  id: string;
  displayName: string;
  description: string | null;
  providerKind: AiProviderKind;
  baseUrl: string;
  /** Model the connection serves, or `null` when the caller and the catalog decide. */
  modelId: string | null;
  /** Non-reversible suffix of the stored credential, for identification; never the key itself. */
  fingerprint: string;
  hasSecret: boolean;
  isVisibleToUsers: boolean;
  isActive: boolean;
  isDeleted: boolean;
  keyVersion: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  deletedAtUtc: string | null;
}

/** Body of `POST /admin/provider-profiles`. */
export interface CreateProviderProfilePayload {
  displayName: string;
  providerKind: AiProviderKind;
  /** Plaintext key to protect; never returned by any endpoint. */
  apiKey: string;
  /** Endpoint; required for every family. */
  baseUrl: string;
  modelId?: string | null;
  description?: string | null;
  isVisibleToUsers: boolean;
}

/**
 * Body of `PUT /admin/provider-profiles/{id}`.
 *
 * An omitted or blank `apiKey` keeps the stored credential, which is what lets the form be submitted without holding the key.
 */
export interface UpdateProviderProfilePayload {
  displayName: string;
  providerKind: AiProviderKind;
  /** Endpoint; required for every family. */
  baseUrl: string;
  modelId?: string | null;
  description?: string | null;
  isVisibleToUsers: boolean;
  apiKey?: string | null;
}

/**
 * The prompt levels a pattern applies to, as a bit mask (`PromptLevelApplicability`, TASK-0039 §4).
 *
 * The wire value is an integer flag set, so a caller combines levels with the bitwise-or and tests one with the bitwise-and:
 * `Simple | Expert` is `1 | 4 = 5`. The names line up with `PromptLevel`'s own values shifted by one bit, and `All` covers
 * every level.
 */
export const PROMPT_LEVEL_APPLICABILITY = {
  None: 0,
  Simple: 1,
  Standard: 2,
  Expert: 4,
  All: 7,
} as const;

/** One prompt pattern as the wizard's picker reads it (`SelectablePromptPatternDto`, TASK-0039 §6). */
export interface SelectablePromptPattern {
  id: string;
  name: string;
  description: string | null;
  /** Generation instruction the pattern contributes, so a picker can preview what it adds. */
  instructions: string;
  /** Bit mask of the levels the pattern applies to. */
  levels: number;
  /** Whether the deterministic policy suggests this pattern for the wizard's current state. */
  isSuggested: boolean;
}

/** One prompt pattern as the administrator panel reads it (`PromptPatternDto`, TASK-0039). */
export interface PromptPattern {
  id: string;
  name: string;
  description: string | null;
  instructions: string;
  /** Bit mask of the levels the pattern applies to. */
  levels: number;
  /** Deterministic ordering weight; lower is suggested earlier. */
  suggestionPriority: number;
  isActive: boolean;
  isDeleted: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  deletedAtUtc: string | null;
}

/** Body of `POST /admin/prompt-patterns`. */
export interface CreatePromptPatternPayload {
  name: string;
  instructions: string;
  /** Bit mask of the levels the pattern applies to; must name at least one. */
  levels: number;
  suggestionPriority: number;
  description?: string | null;
  isActive: boolean;
}

/** Body of `PUT /admin/prompt-patterns/{id}`. */
export interface UpdatePromptPatternPayload {
  name: string;
  instructions: string;
  levels: number;
  suggestionPriority: number;
  description?: string | null;
}

/**
 * One counted value of the public store's discovery dimensions (`StoreFacetDto`, TASK-0065 §3.3).
 *
 * `value` is the key a filter accepts — a registry model identifier such as `claude`, a taxonomy category identifier such
 * as `cat_backend`, or an enum name — and never the label: the store sends the label beside it, and the navigation links
 * with the key, because a translated name is not a filter. A key neither the registry nor the taxonomy knows arrives with
 * both labels `null`, which a menu renders as the raw key rather than as a guessed name.
 */
export interface StoreFacet {
  /** Stable key the store's filters accept; what a link carries. */
  value: string;
  /** Number of published entries carrying the key, as the store counted them. */
  count: number;
  /** Persian label of the key, or `null` when the catalogue has none. */
  labelFa: string | null;
  /** English label of the key, or `null`. */
  labelEn: string | null;
}

/**
 * Licence a published entry is offered under (`PublicationLicence`, domain enum).
 *
 * The members are the backend's own, spelled as it serialises them, and there are exactly six — the browser build does
 * not know a licence the API cannot return.
 */
export const PUBLICATION_LICENCES = [
  'AllRightsReserved',
  'Mit',
  'Apache2',
  'CcBy',
  'CcBySa',
  'Cc0',
] as const;
export type PublicationLicence = (typeof PUBLICATION_LICENCES)[number];

/**
 * Which pricing statements `GET /store` returns (`StorePricingFilter`, TASK-0065 §3.2).
 *
 * `Free` and `Paid` are the *stated* answers only: an entry whose author has not answered the free-or-priced question
 * (BQ-1) is neither, so a free browse does not pad itself with it — which is the rule the client must not contradict by
 * treating a missing price as free.
 */
export const STORE_PRICING_FILTERS = ['Any', 'Free', 'Paid'] as const;
export type StorePricingFilter = (typeof STORE_PRICING_FILTERS)[number];

/**
 * Order `GET /store` serves (`StoreSortOrder`), as the endpoint's own `sort` tokens spell it.
 *
 * `NewestFirst` is the endpoint's default, so it is the order the store's catalogue arrives in unless a caller asks for
 * another one. `RatingDescending` is a legal sort today but says nothing about popularity: every rating counter is still
 * zero (TASK-0065 §3.4), so the store never presents it as a ranking.
 */
export const STORE_SORT_ORDERS = [
  'NewestFirst',
  'OldestFirst',
  'TitleAscending',
  'RatingDescending',
] as const;
export type StoreSortOrder = (typeof STORE_SORT_ORDERS)[number];

/**
 * The card of one published store entry (`StoreListingDto`, TASK-0065 §3.2).
 *
 * The store homepage renders it (TASK-0067) and the browse grid will (TASK-0068). What the card may *say* is decided by
 * this contract and nothing else: `isFree: null` is "no price stated", not "free", and the counters are shown only when
 * they are non-zero, because a zero they would show is a counter that still has no writer rather than a fact.
 */
export interface StoreListing {
  /** Publication identifier the install and rating routes will address. */
  id: string;
  /** URL-stable slug the entry page is addressed by. */
  slug: string;
  titleFa: string;
  titleEn: string | null;
  summaryFa: string;
  summaryEn: string | null;
  domain: PromptDomain;
  taskType: TaskType;
  tags: string[];
  categoryId: string | null;
  targetModelId: string | null;
  promptLevel: PromptLevel;
  licence: PublicationLicence;
  /** Whether the author answered that the entry is free; `null` while the question is unanswered (BQ-1). */
  isFree: boolean | null;
  priceAmount: number | null;
  currency: string | null;
  qualityPassed: boolean;
  qualityScore: number;
  installCount: number;
  viewCount: number;
  ratingCount: number;
  /** Average rating, or `null` while nobody rated the entry. */
  averageRating: number | null;
  authorUserId: string;
  publishedAtUtc: string;
  /**
   * Public handle of the author, or `null` when the account holds none (TASK-0070).
   *
   * A Store page states it so an entry can link to `/profile/{handle}`; the catalogue's own pages leave it `null`, because a
   * card does not need an author address to be useful and resolving an account per card would be a read the listing never
   * promised. `null` is "no address can be given", never "an unknown author".
   */
  authorUsername?: string | null;
}

/** One page of the store catalogue (`StoreListingPageDto`), shaped like the library's paged answers. */
export interface StoreListingPage {
  items: StoreListing[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
}

/**
 * One published entry as its store page reads it (`StoreEntryDto`, TASK-0065 §3.2, consumed by TASK-0069).
 *
 * The card's fields travel inside `publication`, so the page renders the same entry the catalogue advertised and cannot
 * drift from it: there is exactly one place a title, a price or a licence comes from. `markdown` is the body of the
 * newest revision — the text a visitor copies — and `contentHash` identifies precisely what that body was.
 *
 * The review half of the author's package (reviewer, notes, submission instant) is deliberately absent: a visitor never
 * receives it, so no screen has to remember to hide it.
 */
export interface StoreEntry {
  /** Card of the entry: identity, discovery, pricing, quality verdict and counters. */
  publication: StoreListing;
  /** Body of the newest revision, which is the text the page renders and copies. */
  markdown: string;
  /** Library version the publication snapshotted. */
  promptVersion: number;
  /** Monotonic revision of the stored package. */
  revision: number;
  /** SHA-256 hash of the published body. */
  contentHash: string;
  /** Intended audience the author declared, or `null`. */
  audience: string | null;
  /** Output locale of the snapshotted prompt — the language of the body, not of the interface. */
  locale: PromptLocale;
  /** Instant of the last write of the package. */
  updatedAtUtc: string;
}

/**
 * The public profile of one author as `/profile/:username` reads it (`PublicAuthorProfileDto`, TASK-0070).
 *
 * Two facts, and deliberately nothing else: who the account is public by (`username`, `displayName`, `avatarUrl`) and one
 * server-paged list of what it has **published in the Store** (`entries`, with `publishedEntryCount` as the pager's total).
 * There is no biography, no link list and no follower count, because the account model holds none of them — a profile that
 * rendered an empty biography box would advertise a field the product cannot fill.
 *
 * `entries` carries the same eligibility rule as the browse (ADR-0022): a draft, a withdrawn entry and an unlisted one are
 * absent here exactly as they are from the catalogue.
 */
export interface PublicAuthorProfile {
  /** Handle exactly as its owner typed it, which is what the address resolves and what the page prints. */
  username: string;
  displayName: string;
  /** Avatar address, or `null` when the account has none. */
  avatarUrl: string | null;
  /** Number of entries this author has published; the same number `entries.totalCount` carries. */
  publishedEntryCount: number;
  /** One page of the author's published entries, newest first. */
  entries: StoreListingPage;
}

/**
 * The store's discovery answer (`StoreHomepageDto`, TASK-0065 §3.3): every rail the stored documents can prove.
 *
 * The analytics-dependent rails (trending, monthly popular, best sellers, featured) are absent on purpose, because their
 * counters have no writer yet; the store navigation therefore builds its two mega-menus from `models` and `categories`,
 * which are counts over published entries and nothing else. An empty store answers empty arrays and
 * `totalPublishedCount: 0`, and the menus say so instead of inventing rows.
 */
export interface StoreHomepage {
  /** Number of published entries; `0` means the store has nothing to show. */
  totalPublishedCount: number;
  /** Lower bound of the free rail's window as the server applied it (`now − 7 days`). */
  freeWindowFromUtc: string;
  /** Categories ordered by their published-entry count: "popular" means "most published", not an analytics verdict. */
  popularCategories: StoreFacet[];
  freeThisWeek: StoreListing[];
  newest: StoreListing[];
  /** Target models the eligible entries were authored for, with real counts and the registry's label. */
  models: StoreFacet[];
  /** Taxonomy categories the eligible entries are filed under, with real counts and labels. */
  categories: StoreFacet[];
}

/**
 * Why a store wallet moved (`ForgeCreditLedgerKind`, TASK-0072).
 *
 * The store wallet is a *purchase* balance, and this is its vocabulary: it is never negative, unlike the co-pilot's balance,
 * which is published by `/users/me/forge` and may go below zero. Only `SignupReward` and `PurchaseDebit` are written today; the
 * rest exist so the ledger does not have to change shape when top-ups, corrections and refunds arrive.
 */
export const STORE_WALLET_ENTRY_KINDS = [
  'SignupReward',
  'PurchaseDebit',
  'CreditTopUp',
  'AdminAdjustment',
  'Refund',
  'PurchaseReversal',
] as const;
export type StoreWalletEntryKind = (typeof STORE_WALLET_ENTRY_KINDS)[number];

/**
 * One movement of a store wallet (`StoreWalletEntryDto`, TASK-0072 §2.2).
 *
 * `amount` is signed — a credit raised the balance, a debit lowered it — and the two balance snapshots say where it started and
 * where it ended, so a movement explains itself without replaying the history before it.
 */
export interface StoreWalletEntry {
  id: string;
  kind: StoreWalletEntryKind;
  /** Signed amount in whole Forge Credit units. */
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  /** Order the movement belongs to, or `null` for a movement with no order. */
  orderId: string | null;
  /** Recorded reason, or `null`. */
  description: string | null;
  occurredAtUtc: string;
}

/**
 * The caller's store credit (`StoreWalletDto`, TASK-0072 §3.5).
 *
 * `available` is what a checkout may spend: `balance` minus whatever a settlement in flight has reserved. The number is never
 * negative, and a purchase the balance cannot cover is refused rather than allowed to overdraw — the opposite of the co-pilot's
 * ledger, whose balance may legitimately be in debit.
 */
export interface StoreWallet {
  balance: number;
  reserved: number;
  available: number;
  /** Display name of the unit ("Forge"). */
  currencyName: string;
  totalGranted: number;
  totalSpent: number;
  purchaseCount: number;
  /** The most recent committed movements, newest first. */
  recent: StoreWalletEntry[];
}

/** Why a cart line cannot be bought (`StoreCartItemDto.unavailableReason`, TASK-0072 §2.3). */
export type StoreCartUnavailableReason = 'withdrawn' | 'owned';

/**
 * One entry in the caller's cart (`StoreCartItemDto`, TASK-0072 §2.3).
 *
 * The price is the *catalogue's* current price, not one stored with the item: the cart holds identifiers, so a re-priced entry
 * costs its new price and a withdrawn one is shown as unavailable instead of as it was when it was added.
 */
export interface StoreCartItem {
  publicationId: string;
  /** Slug of the entry, so the cart can link to it; empty when the entry is no longer sold. */
  slug: string;
  /** Persian title; empty when the entry is no longer sold. */
  titleFa: string;
  titleEn: string | null;
  isFree: boolean;
  /** Current price in Forge Credit, or `null` for a free entry. */
  priceAmount: number | null;
  currency: string;
  addedAtUtc: string;
  /** Whether the caller already holds an active entitlement for it. */
  isOwned: boolean;
  /** Whether the entry may still be bought. */
  isAvailable: boolean;
  /** Why an unavailable line cannot be bought, or `null`. */
  unavailableReason: StoreCartUnavailableReason | null;
}

/**
 * The caller's cart (`StoreCartDto`, TASK-0072 §2.3, §4.2).
 *
 * Every amount is *informational*: checkout recalculates the subtotal, the discount and the payable amount from the catalogue
 * and refuses a cart it cannot settle, so nothing a client could send changes what is charged.
 */
export interface StoreCart {
  items: StoreCartItem[];
  /** Number of lines that can be bought right now. */
  payableCount: number;
  subtotalAmount: number;
  /** Always `0` while no discount engine exists (TASK-0073). */
  discountAmount: number;
  payableAmount: number;
  currency: string;
  /** The caller's available Forge Credit. */
  balance: number;
  /** Whether a checkout would be accepted: something to buy, everything available, enough credit. */
  canCheckout: boolean;
  isBalanceSufficient: boolean;
}

/** The lifecycle state of a store order (`OrderStatus`, TASK-0072 §2.4); only `Completed` grants anything. */
export const STORE_ORDER_STATUSES = ['Pending', 'Completed', 'Canceled', 'Failed'] as const;
export type StoreOrderStatus = (typeof STORE_ORDER_STATUSES)[number];

/**
 * One purchased item of an order (`StoreOrderItemDto`, TASK-0072 §2.4).
 *
 * A snapshot, so a receipt keeps saying what was bought at the price it was bought at after the author re-prices or withdraws
 * the entry.
 */
export interface StoreOrderItem {
  publicationId: string;
  title: string;
  unitAmount: number;
  currency: string;
  isFree: boolean;
  /** Published revision that was bought. */
  revision: number;
  /** Library version the published revision was taken from. */
  promptVersion: number;
  contentHash: string | null;
  /** Whether the buyer still holds an active entitlement for it. */
  isOwned: boolean;
}

/** One store order (`StoreOrderDto`, TASK-0072 §2.4). `id` is the receipt reference. */
export interface StoreOrder {
  id: string;
  status: StoreOrderStatus;
  items: StoreOrderItem[];
  subtotalAmount: number;
  discountAmount: number;
  payableAmount: number;
  currency: string;
  createdAtUtc: string;
  completedAtUtc: string | null;
}

/** The outcome of a successful checkout (`StoreCheckoutResultDto`, TASK-0072 §4.3). */
export interface StoreCheckoutResult {
  order: StoreOrder;
  /** The buyer's Forge Credit after the settlement. */
  balance: number;
  /** Publications this purchase granted. */
  acquiredPublicationIds: string[];
}

/** One acquired store publication (`StoreOwnedPublicationDto`, TASK-0072 §3.5). */
export interface StoreOwnedPublication {
  publicationId: string;
  orderId: string;
  grantedAtUtc: string;
  /** Slug of the entry while the catalogue still serves it, or `null`. */
  slug: string | null;
  titleFa: string | null;
  titleEn: string | null;
  /** Whether the entry is still on sale; an owned entry stays owned after it is withdrawn. */
  isAvailable: boolean;
}

/** How a discount code reduces a price (`DiscountKind`, TASK-0073, ADR-0030). */
export const DISCOUNT_KINDS = ['Percentage', 'FixedAmount'] as const;
export type DiscountKind = (typeof DISCOUNT_KINDS)[number];

/** Where a discount code is in its own lifecycle (`DiscountCodeState`, TASK-0073). */
export const DISCOUNT_CODE_STATES = ['Active', 'Revoked'] as const;
export type DiscountCodeState = (typeof DISCOUNT_CODE_STATES)[number];

/** Where one attempt to redeem a code stands (`DiscountRedemptionState`, TASK-0073). */
export const DISCOUNT_REDEMPTION_STATES = ['Reserved', 'Consumed', 'Voided'] as const;
export type DiscountRedemptionState = (typeof DISCOUNT_REDEMPTION_STATES)[number];

/**
 * One discount code as an administrator reads it (`AdminDiscountCodeDto`, TASK-0073).
 *
 * Both the cap and the counter travel, because "twelve of fifty used" is the number an operator acts on and a row carrying
 * only the cap would make them open the report to learn it.
 */
export interface AdminDiscountCode {
  id: string;
  code: string;
  kind: DiscountKind;
  /** The percentage, or the number of Forge Credit units. */
  value: number;
  state: DiscountCodeState;
  startsAtUtc: string;
  endsAtUtc: string | null;
  maxRedemptions: number;
  maxRedemptionsPerUser: number;
  /** Slots taken so far, reservations included. */
  redemptionCount: number;
  /** Account the code belongs to, or `null` for a public code. */
  assignedUserId: string | null;
  assignedUserDisplayName: string | null;
  note: string | null;
  createdAtUtc: string;
  revokedAtUtc: string | null;
}

/** One page of the administrative code list (`AdminDiscountCodePageDto`, TASK-0073). */
export interface AdminDiscountCodePage {
  items: AdminDiscountCode[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
}

/** The codes an issue request stored (`AdminDiscountIssueResultDto`, TASK-0073). */
export interface AdminDiscountIssueResult {
  codes: AdminDiscountCode[];
}

/** One redemption as the report lists it (`AdminDiscountRedemptionDto`, TASK-0073). */
export interface AdminDiscountRedemption {
  id: string;
  discountCodeId: string;
  code: string;
  userId: string;
  orderId: string;
  reductionAmount: number;
  state: DiscountRedemptionState;
  reservedAtUtc: string;
  consumedAtUtc: string | null;
  voidedAtUtc: string | null;
}

/** One page of the redemption report (`AdminDiscountRedemptionPageDto`, TASK-0073). */
export interface AdminDiscountRedemptionPage {
  items: AdminDiscountRedemption[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
}

/** One code the caller holds (`MyDiscountCodeDto`, TASK-0073). */
export interface MyDiscountCode {
  code: string;
  kind: DiscountKind;
  value: number;
  startsAtUtc: string;
  endsAtUtc: string | null;
  remainingRedemptions: number;
  /** Whether a checkout would accept it right now. */
  usableNow: boolean;
  note: string | null;
}

/** One page of the caller's Forge Credit movements (`StoreWalletLedgerPageDto`, TASK-0072 §3.5). */
export interface StoreWalletLedgerPage {
  entries: StoreWalletEntry[];
  totalCount: number;
  skip: number;
  take: number;
}

/** One page of the caller's order history (`StoreOrderPageDto`, TASK-0072 §3.5). */
export interface StoreOrderPage {
  items: StoreOrder[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
}
